import React from 'react';

import { Button, Group, NumberInput, Stack, Text } from '@mantine/core';
import { DateInput, TimeInput } from '@mantine/dates';
import { useForm } from '@mantine/form';
import { useShallowEffect } from '@mantine/hooks';
import { useAuth } from 'context/auth-context';
import dayjs, { Dayjs } from 'dayjs';
import useCreateOtherWorkEvent from 'hooks/otherWorkEvent/useCreateOtherWorkEvent';
import { OtherWorkEventWrite } from 'types/generated/models/OtherWorkEventWrite';
import { calculateEventEndDateTime } from 'utils/event.helpers';
import notificationHandler from 'utils/notifications';
import { v4 as uuidv4 } from 'uuid';

import OtherWorkTypeAutoComplete, {
  OtherWorkTypeAutoCompleteValue,
} from './form-fields/auto-complete-fields/OtherWorkTypeAutoComplete';
import StaffAutoComplete, {
  StaffAutoCompleteValue,
} from './form-fields/auto-complete-fields/StaffAutoComplete';

export interface FormValues {
  otherWorkEventId: string;
  otherWorkType: OtherWorkTypeAutoCompleteValue;
  staff: StaffAutoCompleteValue;
  date: Date;
  time: string;
  duration: number;
  dayDiff: string;
  otherWorkEventEnd: Dayjs | null;
}

interface ILogWorkFormProps {
  formId?: string;
  hideSubmitButton?: boolean;
  onSubmitSuccess?: () => void;
}

const LogWorkForm = ({
  formId,
  hideSubmitButton = false,
  onSubmitSuccess,
}: ILogWorkFormProps = {}): JSX.Element => {
  const createMutation = useCreateOtherWorkEvent();
  const { session, hasPerm } = useAuth();

  const canLogOnBehalf = hasPerm('PayrollMutate');

  const form = useForm<FormValues>({
    initialValues: {
      otherWorkEventId: '',
      date: dayjs().toDate(),
      time: dayjs().format('HH:mm').toString(),
      duration: 0,
      otherWorkType: {
        otherWorkTypeId: '',
        value: '',
        label: '',
      },
      staff: {
        staffId: canLogOnBehalf ? '' : session?.staff?.staffId || '',
        label: '',
        value: '',
      },
      dayDiff: '',
      otherWorkEventEnd: null,
    },
    validate: values => ({
      otherWorkType: values.otherWorkType ? null : 'Other work type is required',
      staff: canLogOnBehalf && !values.staff.staffId ? 'Staff is required' : null,
      duration: values.duration <= 0 ? 'Duration must be greater than 0' : null,
    }),
  });

  const handleOtherWorkTypeChange = (item: OtherWorkTypeAutoCompleteValue | null): void => {
    const selectedOtherWorkType = {
      otherWorkTypeId: item ? item.otherWorkTypeId : '',
      value: item ? item.value : '',
      label: item ? item.label : '',
    };
    form.setFieldValue('otherWorkType', selectedOtherWorkType);
  };

  const handleStaffValueChange = (item: StaffAutoCompleteValue | null): void => {
    const selectedStaff = {
      staffId: item ? item.staffId : '',
      value: item ? item.value : '',
      label: item ? item.label : '',
    };
    form.setFieldValue('staff', selectedStaff);
  };

  const handleSubmit = (values: FormValues): void => {
    const { successNotification, errorNotification } = notificationHandler();
    const payload: OtherWorkEventWrite = {
      ...values,
      otherWorkEventId: uuidv4(),
      staff: form.values.staff.staffId,
      otherWorkType: values.otherWorkType.otherWorkTypeId,
      date: dayjs(values.date).format('YYYY-MM-DD'),
    };
    createMutation.mutate(payload, {
      onSuccess: () => {
        successNotification(`Work hours successfully logged.`);
        form.reset();
        onSubmitSuccess?.();
      },
      onError: error => {
        errorNotification('Work hours failed to log.', error.response?.data.message);
      },
    });
  };

  useShallowEffect(() => {
    if (form.values.duration && form.values.time) {
      const otherWorkEventEnd = calculateEventEndDateTime(
        form.values.duration,
        form.values.time,
        form.values.date,
      );
      form.setFieldValue('otherWorkEventEnd', otherWorkEventEnd);
      const start = dayjs(form.values.date);
      const diff = otherWorkEventEnd.date() - start.date();
      let dayDiff = '';
      if (diff === 1) {
        dayDiff = '+1 day';
      }
      if (diff > 1) {
        dayDiff = `+${diff} days`;
      }
      form.setFieldValue('dayDiff', dayDiff);
    } else {
      form.setFieldValue('otherWorkEventEnd', null);
      form.setFieldValue('dayDiff', '');
    }
  }, [form.values.duration, form.values.time, form.values.date]);

  return (
    <form id={formId} onSubmit={form.onSubmit(handleSubmit)}>
      <Stack spacing='xs' p='xs'>
        {canLogOnBehalf && (
          <StaffAutoComplete
            data-cy='otherStaffAutoComplete'
            label='Staff'
            placeholder='Select a staff member'
            value={form.values.staff}
            onChange={handleStaffValueChange}
            error={form.errors.staff}
          />
        )}
        <OtherWorkTypeAutoComplete
          data-cy='otherWorkTypeAutoComplete'
          label='Other Work Type'
          placeholder='Select an other work type'
          value={form.values.otherWorkType}
          error={form.errors.otherWorkType}
          onChange={handleOtherWorkTypeChange}
          required
        />
        <NumberInput
          {...form.getInputProps('duration')}
          label='Duration in minutes'
          placeholder='Enter duration'
          required
          min={15}
          max={60 * 24 * 7}
          data-cy='otherWorkDurationNumberInput'
        />
        <DateInput {...form.getInputProps('date')} label='Date' locale='us' required />
        <Group spacing='xs' grow>
          <TimeInput label='From' {...form.getInputProps('time')} required />
          <TimeInput
            label={
              <Group position='apart'>
                <div>To</div>
                <Text color='red'>{form.values.dayDiff}</Text>
              </Group>
            }
            value={form.values.otherWorkEventEnd ? form.values.otherWorkEventEnd.format('HH:mm') : ''}
            readOnly
            disabled
          />
        </Group>
        {!hideSubmitButton && (
          <Button mt='lg' type='submit' loading={createMutation.isLoading} data-cy='saveOtherWorkButton'>
            Submit
          </Button>
        )}
      </Stack>
    </form>
  );
};

export default LogWorkForm;
